import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  SalespersonActivityStats,
  SubtypeBreakdown,
  WorkItemSubtype,
} from "../../../store/services/salesManagerService";
import { WorkItemTypeEnum } from "../../../store/types/work.types";
import {
  getWorkItemColor,
  getWorkItemIcon,
} from "../../../utils/workItemUtils";
import {
  CATEGORY_LABELS,
  getContactAttemptCount,
  getSubtypeCounts,
  SUBTYPE_LABELS,
  SUBTYPE_ORDER_COMMUNICATION,
  SUBTYPE_ORDER_FOLLOWUP,
  SUBTYPE_TO_WORK_ITEM_TYPE,
} from "../shared/breakdown";

// Förkortade etiketter för kolumnrubriker — tabellen är bred, och
// "Digitalt möte"/"Fysiskt möte" äter onödig horisontell plats.
const SHORT_SUBTYPE_LABELS: Record<WorkItemSubtype, string> = {
  [WorkItemSubtype.Email]: "Email",
  [WorkItemSubtype.PhoneCall]: "Telefon",
  [WorkItemSubtype.Sms]: "SMS",
  [WorkItemSubtype.DigitalMeeting]: "Digital",
  [WorkItemSubtype.PhysicalMeeting]: "Fysisk",
  [WorkItemSubtype.Admin]: "Admin",
  [WorkItemSubtype.Other]: "Övrigt",
  [WorkItemSubtype.ContactAttempt]: "Försök",
};

interface ExecutionByPersonTabProps {
  salespersonStats: SalespersonActivityStats[] | undefined;
  isLoading: boolean;
  onRowClick: (stats: SalespersonActivityStats) => void;
}

type CategoryMode = "followup" | "communication";

type SortKey = "name" | "total" | "contactAttempts" | WorkItemSubtype;
type SortDir = "asc" | "desc";

interface Row {
  stats: SalespersonActivityStats;
  name: string;
  teamName: string | null;
  // Per-subtype completed counts
  counts: Record<WorkItemSubtype, number>;
  total: number;
  // Effort-metrik: antal kontaktförsök (ej del av total).
  contactAttempts: number;
}

const buildRow = (
  stats: SalespersonActivityStats,
  subtypeBreakdown: SubtypeBreakdown,
  subtypeOrder: WorkItemSubtype[]
): Row => {
  const counts = {} as Record<WorkItemSubtype, number>;
  let total = 0;
  subtypeOrder.forEach((subtype) => {
    const done = getSubtypeCounts(subtypeBreakdown, subtype).Completed;
    counts[subtype] = done;
    total += done;
  });
  return {
    stats,
    name: stats.SalespersonName,
    teamName: stats.TeamName,
    counts,
    total,
    // Kontaktförsök är alltid Communication — ta grand-total-cellen.
    contactAttempts: getContactAttemptCount(stats.Breakdown.Total.Total),
  };
};

const ExecutionByPersonTab: React.FC<ExecutionByPersonTabProps> = ({
  salespersonStats,
  isLoading,
  onRowClick,
}) => {
  const theme = useTheme();
  // Mötesbokning (Followup) är default eftersom det representerar faktiskt
  // utfört arbete. Planering är en alternativ vy för den som vill se vad
  // som bara är planerat.
  const [mode, setMode] = useState<CategoryMode>("followup");
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const activeSubtypeOrder =
    mode === "followup" ? SUBTYPE_ORDER_FOLLOWUP : SUBTYPE_ORDER_COMMUNICATION;

  // Kontaktförsök är en Communication-metrik — visas bara i Planering-vyn.
  const showContactAttempts = mode === "communication";

  const rows: Row[] = useMemo(() => {
    if (!salespersonStats) return [];
    return salespersonStats.map((stats) => {
      const subtypeBreakdown =
        mode === "followup"
          ? stats.Breakdown.Total.Followup
          : stats.Breakdown.Total.Communication;
      return buildRow(stats, subtypeBreakdown, activeSubtypeOrder);
    });
  }, [salespersonStats, mode, activeSubtypeOrder]);

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      if (sortKey === "name") {
        return a.name.localeCompare(b.name, "sv");
      }
      if (sortKey === "total") {
        return a.total - b.total;
      }
      if (sortKey === "contactAttempts") {
        return a.contactAttempts - b.contactAttempts;
      }
      return a.counts[sortKey] - b.counts[sortKey];
    });
    return sortDir === "desc" ? sorted.reverse() : sorted;
  }, [rows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
        {[...Array(6)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={56}
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Stack>
    );
  }

  if (!salespersonStats || salespersonStats.length === 0) {
    return (
      <Paper sx={{ p: 6, borderRadius: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Inga säljare hittades
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Justera filtren för att se data
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Kategori-toggle */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
        spacing={2}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            Visar antal <strong>slutförda</strong> aktiviteter per säljare och
            subtyp.
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, value: CategoryMode | null) => {
            if (!value) return;
            setMode(value);
            // Kontaktförsök-kolumnen försvinner i Followup-vyn — fall tillbaka
            // till total-sortering om den var aktiv sorteringsnyckel.
            if (value === "followup" && sortKey === "contactAttempts") {
              setSortKey("total");
            }
          }}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              px: 2,
              fontWeight: 500,
            },
          }}
        >
          <ToggleButton value="followup">
            {CATEGORY_LABELS.Followup}
          </ToggleButton>
          <ToggleButton value="communication">
            {CATEGORY_LABELS.Communication}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                "& th": {
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  py: 1.25,
                },
              }}
            >
              <TableCell
                sortDirection={sortKey === "name" ? sortDir : false}
                sx={{ width: "30%", minWidth: 160 }}
              >
                <TableSortLabel
                  active={sortKey === "name"}
                  direction={sortKey === "name" ? sortDir : "asc"}
                  onClick={() => handleSort("name")}
                  IconComponent={
                    sortDir === "desc" ? ArrowDownIcon : ArrowUpIcon
                  }
                >
                  Säljare
                </TableSortLabel>
              </TableCell>

              {activeSubtypeOrder.map((subtype) => {
                const color = getWorkItemColor(
                  SUBTYPE_TO_WORK_ITEM_TYPE[subtype]
                );
                return (
                  <Tooltip
                    key={subtype}
                    title={SUBTYPE_LABELS[subtype]}
                    arrow
                    placement="top"
                  >
                    <TableCell
                      align="right"
                      sortDirection={sortKey === subtype ? sortDir : false}
                      sx={{ px: 1 }}
                    >
                      <TableSortLabel
                        active={sortKey === subtype}
                        direction={sortKey === subtype ? sortDir : "asc"}
                        onClick={() => handleSort(subtype)}
                        IconComponent={
                          sortDir === "desc" ? ArrowDownIcon : ArrowUpIcon
                        }
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Box
                            sx={{
                              color,
                              display: "flex",
                              "& svg": { fontSize: 16 },
                            }}
                          >
                            {getWorkItemIcon(
                              SUBTYPE_TO_WORK_ITEM_TYPE[subtype]
                            )}
                          </Box>
                          <Box sx={{ fontSize: "0.8rem" }}>
                            {SHORT_SUBTYPE_LABELS[subtype]}
                          </Box>
                        </Stack>
                      </TableSortLabel>
                    </TableCell>
                  </Tooltip>
                );
              })}

              <TableCell
                align="right"
                sortDirection={sortKey === "total" ? sortDir : false}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderLeft: `2px solid ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                  px: 1.5,
                }}
              >
                <TableSortLabel
                  active={sortKey === "total"}
                  direction={sortKey === "total" ? sortDir : "asc"}
                  onClick={() => handleSort("total")}
                  IconComponent={
                    sortDir === "desc" ? ArrowDownIcon : ArrowUpIcon
                  }
                >
                  Totalt
                </TableSortLabel>
              </TableCell>

              {/* Kontaktförsök — fristående effort-kolumn, ej del av Totalt.
                  Visas bara i Planering-vyn (Communication). */}
              {showContactAttempts && (
                <Tooltip
                  title="Kontaktförsök — antal gånger säljaren försökt nå kunden utan att lyckas. Ren ansträngnings­metrik, ingår inte i totalen."
                  arrow
                  placement="top"
                >
                  <TableCell
                    align="right"
                    sortDirection={
                      sortKey === "contactAttempts" ? sortDir : false
                    }
                    sx={{
                      bgcolor: alpha("#9B6B00", 0.06),
                      borderLeft: `1px dashed ${alpha("#9B6B00", 0.4)}`,
                      px: 1.5,
                    }}
                  >
                    <TableSortLabel
                      active={sortKey === "contactAttempts"}
                      direction={sortKey === "contactAttempts" ? sortDir : "asc"}
                      onClick={() => handleSort("contactAttempts")}
                      IconComponent={
                        sortDir === "desc" ? ArrowDownIcon : ArrowUpIcon
                      }
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                          sx={{
                            color: "#9B6B00",
                            display: "flex",
                            "& svg": { fontSize: 16 },
                          }}
                        >
                          {getWorkItemIcon(WorkItemTypeEnum.ContactAttempt)}
                        </Box>
                        <Box sx={{ fontSize: "0.8rem" }}>Försök</Box>
                      </Stack>
                    </TableSortLabel>
                  </TableCell>
                </Tooltip>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow
                key={row.stats.SalespersonId}
                hover
                onClick={() => onRowClick(row.stats)}
                sx={{
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={row.stats.SalespersonImagePath}
                      sx={{ width: 28, height: 28, fontSize: "0.85rem" }}
                    >
                      {row.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                        }}
                      >
                        {row.name}
                      </Typography>
                      {row.teamName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {row.teamName}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>

                {activeSubtypeOrder.map((subtype) => {
                  const value = row.counts[subtype];
                  return (
                    <TableCell
                      key={subtype}
                      align="right"
                      sx={{
                        px: 1,
                        color: value === 0 ? "text.disabled" : "text.primary",
                        fontWeight: value > 0 ? 500 : 400,
                      }}
                    >
                      {value}
                    </TableCell>
                  );
                })}

                <TableCell
                  align="right"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderLeft: `2px solid ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                    fontWeight: 700,
                    px: 1.5,
                  }}
                >
                  {row.total}
                </TableCell>

                {/* Kontaktförsök — effort, dämpad amber */}
                {showContactAttempts && (
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: alpha("#9B6B00", 0.04),
                      borderLeft: `1px dashed ${alpha("#9B6B00", 0.4)}`,
                      px: 1.5,
                      color:
                        row.contactAttempts === 0 ? "text.disabled" : "#9B6B00",
                      fontWeight: row.contactAttempts > 0 ? 600 : 400,
                    }}
                  >
                    {row.contactAttempts}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ExecutionByPersonTab;
